import * as Sentry from "@sentry/react";
import type { Static, TSchema } from "typebox";
import type { Validator } from "typebox/compile";
import {
  bootstrapChecker,
  getBootstrap,
  setCsrfToken,
} from "@/shared/bootstrap/bootContract.ts";
import {
  endReadRequest,
  endSaveRequest,
  failedSaveRequest,
  startReadRequest,
  startSaveRequest,
} from "@/shared/queries/networkState.ts";
import { getCvPartner } from "@/shared/util/getCvPartner.ts";
import { isArrayOfLength } from "@/shared/util/invariant.ts";
import { isNotEmpty } from "@/shared/util/value.ts";

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
type PassedValidator<T extends TSchema> = Validator<{}, T>;

const csrfTokenHeaderName = "X-CSRF-Token";
const pusherSessionIdHeaderName = "X-PUSHER-SESSION-ID";

interface TokenHeaders {
  [csrfTokenHeaderName]?: string;
  [pusherSessionIdHeaderName]?: string;
}

export function getCsrfToken(): string {
  return getBootstrap().csrf_token;
}

export function getTokenHeaders(): TokenHeaders {
  return tokenHeadersWith(getCsrfToken());
}

function tokenHeadersWith(csrfToken: string): TokenHeaders {
  const tokenHeaders: TokenHeaders = {
    [csrfTokenHeaderName]: csrfToken,
  };

  const pusherSessionId = getCvPartner().subscriber?.session_id();

  if (pusherSessionId) {
    tokenHeaders[pusherSessionIdHeaderName] = pusherSessionId;
  }

  return tokenHeaders;
}

interface BaseFetchOptions<TBody extends object = object> {
  body?: TBody | FormData;
  safe?: boolean;
  timeout?: number;
  signal?: AbortSignal;
  skipLoadingIndicator?: boolean;
}

interface CheckedFetchOptions<
  TBody extends object = object,
> extends BaseFetchOptions<TBody> {
  plainText?: boolean;
}

interface FetchOptions<
  TBody extends object = object,
> extends CheckedFetchOptions<TBody> {
  ignoreResponseBody?: boolean;
}

type FetchMethod = "POST" | "GET" | "PUT" | "PATCH" | "DELETE";

function getRequestString(url: URL | string, method: string) {
  const urlInError =
    // eslint-disable-next-line unicorn/prefer-minimal-ternary
    typeof url === "string"
      ? new URL(url, window.location.origin).pathname
      : url.pathname;

  return `${method} ${urlInError}`;
}

export class FetchError extends Error {
  readonly status: number;
  readonly statusText: string;
  readonly body: string | undefined;
  readonly parsedBody: unknown | undefined;

  constructor(
    method: string,
    url: URL | string,
    status: number,
    statusText: string,
    body: string | undefined,
    parsedBody: unknown | undefined,
  ) {
    const requestString = getRequestString(url, method);
    const formattedStatusText = statusText ? ` "${statusText}"` : "";

    super(
      `${requestString} failed with status ${status}${formattedStatusText}`,
    );

    this.status = status;
    this.statusText = statusText;
    this.body = body;
    this.parsedBody = parsedBody;
  }
}

export class FetchTimeout extends Error {
  constructor(method: FetchMethod, url: URL | string, timeout: number) {
    super(`${getRequestString(url, method)} timed out after ${timeout}ms`);
  }
}

class FetchNetworkError extends Error {
  constructor(method: FetchMethod, url: URL | string, cause: unknown) {
    super(`${getRequestString(url, method)} got an error`, { cause });
  }
}

export class FetchAborted extends Error {
  constructor(method: FetchMethod, url: URL | string, reason: unknown) {
    super(`${getRequestString(url, method)} was aborted`, { cause: reason });
  }
}

export class FetchDuringUnload extends FetchAborted {
  constructor(method: FetchMethod, url: URL | string, cause: unknown) {
    super(method, url, cause);
    this.message = `${getRequestString(url, method)} failed while the page unloaded`;
  }
}

type Timeout = ReturnType<typeof globalThis.setTimeout>;

interface TimeoutOptions {
  signal: AbortSignal | undefined;
  timeoutId: Timeout | undefined;
}

interface InternalFetchOptions extends TimeoutOptions {
  url: URL | string;
  method: FetchMethod;
  headers: HeadersInit;
  body: string | FormData | undefined;
}

interface InternalFetchBufferedOptions extends InternalFetchOptions {
  plainText: boolean;
  ignoreResponseBody: boolean;
}

async function callFetch({
  url,
  method,
  headers,
  body,
  signal,
}: InternalFetchOptions): Promise<Response> {
  try {
    return await fetch(url, { method, headers, body, signal });
  } catch (error) {
    throw new FetchNetworkError(method, url, error);
  }
}

async function makeRequest(options: InternalFetchOptions) {
  const res = await callFetch(options);

  const { url, method } = options;

  if (!res.ok) {
    let body: string | undefined = undefined;
    let parsedBody: unknown | undefined = undefined;

    try {
      body = await res.text();

      const contentType = res.headers.get("content-type");

      if (contentType && contentType.includes("application/json")) {
        parsedBody = JSON.parse(body);
      }
    } catch {
      // ignore
    }

    throw new FetchError(
      method,
      url,
      res.status,
      res.statusText,
      body,
      parsedBody,
    );
  }

  if (res.status === 204) {
    return null;
  }

  return res;
}

// The browser kills in-flight requests when the page unloads without touching
// the caller's signal.
let pageUnloading = false;

export function trackPageUnload() {
  const hide = () => {
    pageUnloading = true;
  };
  const show = () => {
    pageUnloading = false;
  };

  window.addEventListener("pagehide", hide);
  window.addEventListener("pageshow", show);

  return () => {
    window.removeEventListener("pagehide", hide);
    window.removeEventListener("pageshow", show);
    pageUnloading = false;
  };
}

function abortErrorFor(
  error: unknown,
  {
    signal,
    method,
    url,
  }: Pick<InternalFetchOptions, "signal" | "method" | "url">,
): unknown {
  if (signal?.aborted) {
    if (signal.reason instanceof FetchTimeout) return signal.reason;

    return new FetchAborted(method, url, signal.reason);
  }

  if (pageUnloading) return new FetchDuringUnload(method, url, error);

  return error;
}

// An abort that lands after the headers rejects the body read, not the fetch,
// so the catch has to span both, and the timeout stays armed until the body
// has been read.
async function makeBufferedRequest(options: InternalFetchBufferedOptions) {
  try {
    const res = await makeRequest(options);

    if (!res || options.ignoreResponseBody) {
      return null;
    }

    if (options.plainText) {
      return await res.text();
    }

    return await res.json();
  } catch (error) {
    throw abortErrorFor(error, options);
  } finally {
    if (options.timeoutId) {
      clearTimeout(options.timeoutId);
    }
  }
}

function createTimeout(
  method: FetchMethod,
  url: URL | string,
  timeout: FetchOptions["timeout"],
): TimeoutOptions & { controller: AbortController | undefined } {
  let signal: AbortSignal | undefined = undefined;
  let timeoutId: Timeout | undefined = undefined;
  let controller: AbortController | undefined = undefined;

  if (timeout) {
    const actualController = new AbortController();
    controller = actualController;
    signal = controller.signal;

    timeoutId = setTimeout(() => {
      actualController.abort(new FetchTimeout(method, url, timeout));
    }, timeout);
  }

  return { signal, timeoutId, controller };
}

const jsonMethods = new Set(["POST", "PUT", "PATCH"]);

async function performFetch<T = unknown, TBody extends object = object>(
  method: FetchMethod,
  url: URL | string,
  options: FetchOptions<TBody> = {},
): Promise<T> {
  const {
    timeout,
    plainText,
    ignoreResponseBody,
    safe,
    signal: incomingSignal,
    skipLoadingIndicator,
  } = options;

  const headers: HeadersInit = {
    accept: plainText ? "text/plain" : "application/json",
    // This is needed for Rails to detect that this is an XHR request
    "X-Requested-With": "XMLHttpRequest",
  };

  let body: string | FormData | undefined = undefined;

  if (options.body instanceof FormData) {
    body = options.body;
  } else if (options.body && jsonMethods.has(method)) {
    headers["content-type"] = "application/json";
    body = JSON.stringify(options.body);
  }

  const isReadRequest = method === "GET" || safe;

  if (!skipLoadingIndicator) {
    if (isReadRequest) {
      startReadRequest();
    } else {
      startSaveRequest();
    }
  }

  const send = (csrfToken: string) => {
    let { signal, timeoutId } = createTimeout(method, url, timeout);

    if (incomingSignal) {
      signal = signal
        ? abortSignalAny([signal, incomingSignal])
        : incomingSignal;
    }

    return makeBufferedRequest({
      url,
      method,
      headers: { ...headers, ...tokenHeadersWith(csrfToken) },
      body,
      signal,
      timeoutId,
      plainText: plainText ?? false,
      ignoreResponseBody: ignoreResponseBody ?? false,
    });
  };

  const request = retryWithCurrentCsrfToken(method, url, incomingSignal, send);

  if (skipLoadingIndicator) {
    return request;
  }

  if (isReadRequest) {
    try {
      return await request;
    } finally {
      endReadRequest();
    }
  }

  try {
    const res = await request;

    endSaveRequest();

    return res;
  } catch (error) {
    failedSaveRequest();

    throw error;
  }
}

function bindFetch(method: FetchMethod) {
  return async function fetch<T, TBody extends object = object>(
    url: URL | string,
    options?: FetchOptions<TBody>,
  ) {
    return await performFetch<T, TBody>(method, url, options);
  };
}

export const http = {
  get: bindFetch("GET"),
  post: bindFetch("POST"),
  patch: bindFetch("PATCH"),
  put: bindFetch("PUT"),
  delete: bindFetch("DELETE"),
};

function bindCheckedFetch(method: FetchMethod) {
  return async function checkedFetch<
    T extends TSchema,
    TBody extends object = object,
  >(
    url: URL | string,
    checker: PassedValidator<T>,
    options?: CheckedFetchOptions<TBody>,
  ) {
    const fetchResult = await performFetch<unknown, TBody>(
      method,
      url,
      options,
    );

    return checker.Decode(fetchResult);
  };
}

export const httpChecked = {
  get: bindCheckedFetch("GET"),
  post: bindCheckedFetch("POST"),
  patch: bindCheckedFetch("PATCH"),
  put: bindCheckedFetch("PUT"),
  delete: bindCheckedFetch("DELETE"),
};

// Rails answers a write whose CSRF token no longer matches the session with a
// 401 while reads keep working: the session was reset behind this page.
async function retryWithCurrentCsrfToken<T>(
  method: FetchMethod,
  url: URL | string,
  signal: AbortSignal | undefined,
  send: (csrfToken: string) => Promise<T>,
): Promise<T> {
  const sentToken = getCsrfToken();

  try {
    return await send(sentToken);
  } catch (error) {
    const staleTokenSuspected =
      method !== "GET" &&
      !signal?.aborted &&
      error instanceof FetchError &&
      error.status === 401;

    if (!staleTokenSuspected) throw error;

    // Another request's recovery already replaced the token this one carried.
    const currentToken = getCsrfToken();
    if (currentToken !== sentToken) return send(currentToken);

    const recovered = await unlessAborted(
      recoverCsrfToken(),
      signal,
      method,
      url,
    );

    if (!recovered) throw error;

    return send(getCsrfToken());
  }
}

// Ends this caller's wait on the shared recovery; the recovery itself runs on
// for the other requests waiting on it.
async function unlessAborted<T>(
  promise: Promise<T>,
  signal: AbortSignal | undefined,
  method: FetchMethod,
  url: URL | string,
): Promise<T> {
  if (!signal) return promise;
  if (signal.aborted) throw new FetchAborted(method, url, signal.reason);

  const aborted = new Promise<never>((_resolve, reject) => {
    signal.addEventListener(
      "abort",
      () => reject(new FetchAborted(method, url, signal.reason)),
      { once: true },
    );
  });

  return Promise.race([promise, aborted]);
}

const csrfTokenRecoveryTimeout = 10_000;

let csrfTokenRecovery: Promise<boolean> | undefined;

async function recoverCsrfToken(): Promise<boolean> {
  csrfTokenRecovery ??= fetchCurrentCsrfToken();

  try {
    return await csrfTokenRecovery;
  } finally {
    csrfTokenRecovery = undefined;
  }
}

async function fetchCurrentCsrfToken(): Promise<boolean> {
  try {
    const bootstrap = await httpChecked.get("/bootstrap", bootstrapChecker, {
      skipLoadingIndicator: true,
      timeout: csrfTokenRecoveryTimeout,
    });

    if (!bootstrap.logged_in) return false;

    // Another tab may have signed in as someone else; this page's writes must
    // not go through under that user.
    if (bootstrap.current_user_id !== getBootstrap().current_user_id) {
      return false;
    }

    setCsrfToken(bootstrap.csrf_token);

    return true;
  } catch {
    return false;
  }
}

/**
 * Use this if you're unsure if the response is valid for the types you've defined.
 */
function bindWarningCheckedFetch(method: FetchMethod) {
  return async function checkedFetch<
    T extends TSchema,
    TBody extends object = object,
  >(
    url: URL | string,
    checker: PassedValidator<T>,
    options?: CheckedFetchOptions<TBody>,
  ) {
    const fetchResult = await performFetch<unknown, TBody>(
      method,
      url,
      options,
    );

    if (checker.Check(fetchResult)) {
      return fetchResult;
    }

    const validationErrors = [...checker.Errors(fetchResult)];
    Sentry.captureException(new Error(`${url} - Failed to decode response`), {
      tags: { url: url.toString() },
      extra: { fetchResult, validationErrors },
    });

    return fetchResult as Static<T>;
  };
}

export const httpWarnChecked = {
  get: bindWarningCheckedFetch("GET"),
  post: bindWarningCheckedFetch("POST"),
  patch: bindWarningCheckedFetch("PATCH"),
  put: bindWarningCheckedFetch("PUT"),
  delete: bindWarningCheckedFetch("DELETE"),
};

// Not supported in all browsers: https://developer.mozilla.org/en-US/docs/Web/API/AbortSignal/any_static#browser_compatibility
function abortSignalAny(incomingSignals: readonly (AbortSignal | undefined)[]) {
  const signals = incomingSignals.filter(isNotEmpty);

  if (typeof AbortSignal.any === "function") {
    return AbortSignal.any(signals);
  }

  if (signals.length === 0) {
    const controller = new AbortController();
    return controller.signal;
  }

  if (isArrayOfLength(signals, 1)) return signals[0];

  for (const signal of signals) {
    if (signal.aborted) return signal;
  }

  const controller = new AbortController();

  const aborters: (() => void)[] = [];

  for (const signal of signals) {
    const abort = () => {
      controller.abort(signal.reason);

      for (const aborter of aborters) aborter();
    };

    signal.addEventListener("abort", abort);
    aborters.push(() => signal.removeEventListener("abort", abort));
  }

  return controller.signal;
}
